<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from '../signal/CollapsibleCard.vue'
import type { GatedFuturesSignal } from '../../api/client'

// Generalised 2026-10-07 (Charteris Phase 2.5, Task 4) from NiyMoveCard.vue:
// any 08:40-decision-cutoff gated-futures fund's move card. JAPAN's NIY
// instance is now `NiyMoveCard.vue`, a thin wrapper around this component;
// CHARTERIS's SI instance follows in Task 5. All fund-specific wording
// (reason text, anchor-note sentence, month formatting) is injected via
// props/callbacks rather than hard-coded here.
const props = defineProps<{
  signal: GatedFuturesSignal
  todayLondon: string
  symbol: string
  anchorLabel: string
  testId: string
  describe: (s: GatedFuturesSignal) => { text: string; tone: 'ok' | 'amber' }
  anchorNote?: (s: GatedFuturesSignal, todayLondon: string) => string | null
}>()

const reasonInfo = computed(() => props.describe(props.signal))
const anchorNoteText = computed(() => props.anchorNote?.(props.signal, props.todayLondon) ?? null)

const title = computed(() => `${props.symbol} move at 08:40`)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Manual day+month formatter (not Intl) so the label is locale-stable —
// Node/ICU renders en-GB 'short' September as "Sept". Moved verbatim from
// NiyMoveCard.vue (which imported the equivalent from japanReasons.ts).
function fmtDayMon(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]}`
}

const anchorDateLabel = computed(() => (props.signal.anchor_session ? fmtDayMon(props.signal.anchor_session) : '—'))

// Same reason text IS the collapsed one-line summary (as in NiyMoveCard) so
// there is no separate summary string to drift from the `describe` callback.
const summary = computed(() => reasonInfo.value.text)

const fmtNum = (n: number | null | undefined): string => (n == null ? '—' : n.toLocaleString('en-GB'))

const LONDON_HHMM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

const LONDON_ISO_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
})

const decisionTime = computed(() => {
  const ts = props.signal.decision_price_ts_utc
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  // London wall-clock (final-review M2) — the rest of the card is London time.
  // 2026-10-02: a previous day's print is dated so it cannot pass for today's.
  const day = LONDON_ISO_DATE.format(d)
  const hhmm = `${LONDON_HHMM.format(d)} London`
  return day === props.todayLondon ? hhmm : `${hhmm} on ${fmtDayMon(day)}`
})

// Gauge: a horizontal bar spanning [-2t, +2t] bp with the current move and
// a tick at the threshold on each side. Hidden entirely when either input
// is missing so it never renders a NaN-derived position. Moved verbatim
// from NiyMoveCard.vue.
const gauge = computed(() => {
  const m = props.signal.m_0840
  const t = props.signal.threshold_bps
  if (m == null || t == null || t <= 0) return null
  const bp = m * 10000
  const range = 4 * t // -2t .. +2t
  const clamp = (v: number) => Math.min(100, Math.max(0, v))
  const toPct = (v: number) => clamp(((v + 2 * t) / range) * 100)
  return {
    valuePct: toPct(bp),
    thresholdPosPct: toPct(t),
    thresholdNegPct: toPct(-t),
    zeroPct: toPct(0),
  }
})
</script>

<template>
  <CollapsibleCard :title="title" :summary="summary" :alert="!!signal.degraded" :testid="testId">
    <div class="fm-card" :class="reasonInfo.tone === 'amber' ? 'fm-amber' : 'fm-ok'">
      <div class="fm-reason" :class="reasonInfo.tone === 'amber' ? 'fm-reason-amber' : 'fm-reason-ok'">
        {{ reasonInfo.text }}
      </div>

      <div v-if="gauge" class="fm-gauge">
        <div class="fm-gauge-track">
          <div class="fm-gauge-zero" :style="{ left: gauge.zeroPct + '%' }" />
          <div class="fm-gauge-tick" :style="{ left: gauge.thresholdNegPct + '%' }" />
          <div class="fm-gauge-tick" :style="{ left: gauge.thresholdPosPct + '%' }" />
          <div class="fm-gauge-marker" :style="{ left: gauge.valuePct + '%' }" />
        </div>
      </div>

      <div class="fm-line" :title="`Anchor: ${anchorLabel}`" :aria-label="`Anchor: ${anchorLabel}`">
        Anchor {{ anchorDateLabel }} close {{ fmtNum(signal.anchor_price) }}
      </div>
      <div class="fm-line">
        08:40 print {{ fmtNum(signal.decision_price) }}<template v-if="decisionTime"> at {{ decisionTime }}</template>
      </div>
      <div v-if="anchorNoteText" class="fm-anchor-note">{{ anchorNoteText }}</div>
    </div>
  </CollapsibleCard>
</template>

<style scoped>
.fm-card {
  border-radius: 4px;
  padding: 6px 8px;
  background: #0d1117;
  border-left: 3px solid #30363d;
}
.fm-ok { border-left-color: #3fb950; }
.fm-amber { border-left-color: #d29922; }

.fm-reason { font-size: 0.85rem; }
.fm-reason-ok { color: #c9d1d9; }
.fm-reason-amber { color: #d29922; }

.fm-gauge { margin: 8px 0; }
.fm-gauge-track {
  position: relative;
  height: 6px;
  border-radius: 3px;
  background: #21262d;
}
.fm-gauge-zero {
  position: absolute;
  top: -2px;
  width: 1px;
  height: 10px;
  background: #484f58;
  transform: translateX(-0.5px);
}
.fm-gauge-tick {
  position: absolute;
  top: -2px;
  width: 2px;
  height: 10px;
  background: #6e7681;
  transform: translateX(-1px);
}
.fm-gauge-marker {
  position: absolute;
  top: -3px;
  width: 8px;
  height: 12px;
  border-radius: 2px;
  background: #58a6ff;
  transform: translateX(-4px);
}

.fm-line { font-size: 0.78rem; color: #8b949e; margin-top: 2px; }
.fm-anchor-note { font-size: 0.75rem; color: #d29922; margin-top: 4px; font-style: italic; }

@media (max-width: 700px) {
  .fm-reason { font-size: 0.8rem; }
  .fm-line { font-size: 0.72rem; }
}
</style>
